import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiZap } from 'react-icons/fi';

import { AI_MESSAGE_ROLES, AI_SUGGESTION_TYPES } from '@highlight-cp/constants';
import { Alert, Button, Grid, Stack, Text } from '@highlight-cp/core-ui';

import type {
  AddMessageOptions,
  LegacyChatMessage,
  SuggestionItem,
  Suggestions,
} from '../../ai-chat-types';
import PromptButton from '../../prompt-button/prompt-button';
import {
  applyLogisticsSelectionRules,
  getLogisticsTypesFromChoice,
  isLogisticsSelectionGroup as getIsLogisticsSelectionGroup,
} from './logistics-selection';
import { useTranslation } from 'next-i18next/pages';

interface SuggestionsWidgetProps {
  suggestions: Suggestions;
  onAddMessage: (message: LegacyChatMessage, options?: AddMessageOptions) => void;
}

const SuggestionsWidget = ({ suggestions, onAddMessage }: SuggestionsWidgetProps) => {
  const { t } = useTranslation();
  const [selectedSuggestionLabels, setSelectedSuggestionLabels] = useState<string[]>([]);
  const [selectedSuggestionLabel, setSelectedSuggestionLabel] = useState<string | null>(null);
  const logisticsTypesByLabel = useMemo(
    () =>
      new Map(
        suggestions.choices.map((choice) => [
          choice.label,
          {
            choice,
            logisticsTypes: getLogisticsTypesFromChoice(choice),
          },
        ]),
      ),
    [suggestions.choices],
  );

  const isLogisticsSelectionGroup = useMemo(
    () =>
      getIsLogisticsSelectionGroup(suggestions.selectionGroup, [...logisticsTypesByLabel.values()]),
    [logisticsTypesByLabel, suggestions.selectionGroup],
  );

  const isMultiSelectMode = suggestions.allowMultipleSelection || isLogisticsSelectionGroup;

  useEffect(() => {
    setSelectedSuggestionLabel(null);
    setSelectedSuggestionLabels([]);
  }, [isMultiSelectMode, suggestions.choices, suggestions.type]);

  const isSelected = useCallback(
    (label: string) => {
      return isMultiSelectMode
        ? selectedSuggestionLabels.includes(label)
        : selectedSuggestionLabel === label;
    },
    [isMultiSelectMode, selectedSuggestionLabel, selectedSuggestionLabels],
  );

  const handleSelect = useCallback(
    (suggestionItem: SuggestionItem) => {
      if (isMultiSelectMode) {
        setSelectedSuggestionLabels((currentLabels) => {
          const isAlreadySelected = currentLabels.includes(suggestionItem.label);
          if (isAlreadySelected) {
            return currentLabels.filter((label) => label !== suggestionItem.label);
          }

          const labelsWithoutCurrent = currentLabels.filter(
            (label) => label !== suggestionItem.label,
          );
          if (!isLogisticsSelectionGroup) {
            return [...labelsWithoutCurrent, suggestionItem.label];
          }

          return applyLogisticsSelectionRules(
            currentLabels,
            suggestionItem.label,
            logisticsTypesByLabel,
          );
        });
        return;
      }

      // Both BUTTON and PROMPT types use the prompt field for the message sent to AI
      // The prompt field is required and contains detailed instructions hidden from the user
      onAddMessage(
        {
          role: AI_MESSAGE_ROLES.USER,
          content: suggestionItem.prompt,
        },
        { hideMessage: true },
      );

      setSelectedSuggestionLabel(suggestionItem.label);
    },
    [isLogisticsSelectionGroup, isMultiSelectMode, logisticsTypesByLabel, onAddMessage],
  );

  const handleConfirmSelection = useCallback(() => {
    if (!isMultiSelectMode || selectedSuggestionLabels.length === 0) {
      return;
    }

    const selectedChoices = selectedSuggestionLabels
      .map((label) => logisticsTypesByLabel.get(label))
      .filter((item): item is { choice: SuggestionItem; logisticsTypes: string[] } =>
        Boolean(item),
      );

    if (isLogisticsSelectionGroup) {
      const logisticsTypes = Array.from(
        new Set(selectedChoices.flatMap((choice) => choice.logisticsTypes)),
      );

      onAddMessage(
        {
          role: AI_MESSAGE_ROLES.USER,
          content: `The user selected logistics options. Set logisticType to [${logisticsTypes.map((value) => `'${value}'`).join(',')}] and call updateProjectRequirements.`,
        },
        { hideMessage: true },
      );
      return;
    }

    onAddMessage(
      {
        role: AI_MESSAGE_ROLES.USER,
        content: `The user selected multiple options. Apply all instructions:\n${selectedChoices.map(({ choice }) => `- ${choice.prompt}`).join('\n')}`,
      },
      { hideMessage: true },
    );
  }, [
    isLogisticsSelectionGroup,
    isMultiSelectMode,
    logisticsTypesByLabel,
    onAddMessage,
    selectedSuggestionLabels,
  ]);

  const infoText = suggestions.info?.trim() ?? '';

  return (
    <Stack align="center" gap="sm">
      {isLogisticsSelectionGroup && isMultiSelectMode && (
        <Text size="sm">
          {t(
            'Select how participants should receive products below. You can combine self-managed shipping and participant pickup in the same project.',
          )}
        </Text>
      )}
      <Grid justify="center" gap={suggestions.type === AI_SUGGESTION_TYPES.BUTTON ? 'xs' : 'md'}>
        {suggestions.choices.map((choice) => {
          switch (suggestions.type) {
            case AI_SUGGESTION_TYPES.BUTTON: {
              const choiceSelected = isSelected(choice.label);
              return (
                <Grid.Col key={choice.label} span={1} maw="100%">
                  <Button
                    variant={choiceSelected ? 'filled' : 'outline'}
                    leftSection={<FiZap size="20" />}
                    onClick={() => handleSelect(choice)}
                    size="xs"
                  >
                    {choice.label}
                  </Button>
                </Grid.Col>
              );
            }
            case AI_SUGGESTION_TYPES.PROMPT:
              return (
                <Grid.Col key={choice.label} span={4}>
                  <PromptButton
                    label={choice.label}
                    value={choice.value ?? ''}
                    isSelected={isSelected(choice.label)}
                    disableWhenSelected={!isMultiSelectMode}
                    onClick={() => handleSelect(choice)}
                  />
                </Grid.Col>
              );
            default:
              return null;
          }
        })}
      </Grid>
      {isMultiSelectMode && (
        <Button
          variant="filled"
          size="xs"
          onClick={handleConfirmSelection}
          disabled={selectedSuggestionLabels.length === 0}
        >
          {suggestions.confirmButtonLabel ?? 'Confirm selection'}
        </Button>
      )}
      {infoText ? (
        <Alert color="blue" variant="transparent" p={0} role="note">
          {infoText}
        </Alert>
      ) : null}
    </Stack>
  );
};

export default SuggestionsWidget;
